
import { Book, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import PageLayout from "@/components/layout/PageLayout";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";

const AboutUs = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("about");
  const [imagesLoaded, setImagesLoaded] = useState<{[key: string]: boolean}>({
    eugen: false,
    trine: false
  });

  const handleImageLoad = (imageName: string) => {
    setImagesLoaded(prev => ({
      ...prev,
      [imageName]: true
    }));
  };

  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <PageLayout hideHeader>
      {/* Mission & Values Section */}
      <div className="bg-gradient-to-br from-purple-200 via-purple-100 to-indigo-200 pt-16 pb-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Mission Column */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="text-center md:text-left">
              <h2 className="text-3xl font-bold mb-6 text-center text-purple-600">
                {t("about.mission.titleMission")}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center">
                {t("about.mission.descriptionMission")}
              </p>
            </motion.div>
            
            {/* Values Column */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="text-center md:text-left">
              <h2 className="text-3xl font-bold mb-6 text-center text-violet-600">
                {t("about.mission.titleValues")}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center">
                {t("about.mission.descriptionValues")}
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Our Story Section - Enhanced with visual elements and broader layout */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="max-w-4xl mx-auto border border-gray-200 rounded-lg p-8 bg-white shadow-sm">
            <div className="flex items-center justify-center mb-6">
              <div className="h-1 bg-gradient-to-r from-purple-300 to-indigo-300 w-16 mr-4 rounded"></div>
              <h2 className="text-3xl font-bold text-purple-600">
                {t("about.story.title")}
              </h2>
              <div className="h-1 bg-gradient-to-r from-indigo-300 to-purple-300 w-16 ml-4 rounded"></div>
            </div>
            
            <div className="space-y-6 text-gray-700">
              {t("about.story.content").split('\n\n').map((paragraph, index) => <div key={index} className="relative">
                  <p className="text-lg leading-relaxed mb-6">{paragraph}</p>
                  {index < t("about.story.content").split('\n\n').length - 1}
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Team Section */}
      <div className="bg-gradient-to-br from-indigo-200 via-purple-100 to-purple-200 py-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">{t("about.team.title")}</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <div className="flex flex-col items-center text-center">
              <div className="w-48 h-48 rounded-full bg-white shadow-lg flex items-center justify-center border-4 border-white overflow-hidden mb-6 relative">
                {!imagesLoaded.eugen && (
                  <Skeleton className="absolute inset-0 rounded-full" />
                )}
                <img 
                  alt="Eugen Larsen" 
                  className={`w-full h-full object-[center_15%] object-cover ${imagesLoaded.eugen ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`} 
                  src="/lovable-uploads/8991f69b-63e8-44f3-a249-510e0bdeb438.jpg" 
                  loading="lazy"
                  onLoad={() => handleImageLoad('eugen')}
                />
              </div>
              <h3 className="font-bold text-2xl">{t("about.team.eugen.name")}</h3>
              <p className="text-gray-600 max-w-sm mt-4">{t("about.team.eugen.description")}</p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-48 h-48 rounded-full bg-white shadow-lg flex items-center justify-center border-4 border-white overflow-hidden mb-6 relative">
                {!imagesLoaded.trine && (
                  <Skeleton className="absolute inset-0 rounded-full" />
                )}
                <img 
                  alt="Trine Larsen" 
                  className={`w-full h-full object-cover object-[center_65%] ${imagesLoaded.trine ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`} 
                  src="/lovable-uploads/edde89cb-4c1e-4883-a4c8-685cf6c6cad7.jpg" 
                  loading="lazy"
                  onLoad={() => handleImageLoad('trine')}
                />
              </div>
              <h3 className="font-bold text-2xl">{t("about.team.trine.name")}</h3>
              <p className="text-gray-600 max-w-sm mt-4">{t("about.team.trine.description")}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </PageLayout>;
};
export default AboutUs;
